import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { Helmet } from 'react-helmet';
import { auditService } from '../../services/auditService';

import { exportToCSV, exportToPDF } from '../../utils/exportUtils';
import Pagination from '../../components/ui/Pagination';

const ACTION_COLORS = {
  CREATE: 'bg-emerald-100 text-emerald-700',
  UPDATE: 'bg-blue-100 text-blue-700',
  DELETE: 'bg-red-100 text-red-700',
  APPROVE: 'bg-violet-100 text-violet-700',
  REJECT: 'bg-orange-100 text-orange-700',
  LOGIN: 'bg-slate-100 text-slate-600',
  EXPORT: 'bg-cyan-100 text-cyan-700',
};

const ENTITY_ICONS = {
  partner: 'Users',
  opportunity: 'TrendingUp',
  risk: 'AlertTriangle',
  capa_action: 'ListChecks',
  lifecycle_request: 'GitBranch',
  portfolio_product: 'Package',
  portfolio_service: 'Wrench',
  training: 'GraduationCap',
  system: 'Settings',
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr)?.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return new Date(dateStr)?.toLocaleDateString();
};

const ITEMS_PER_PAGE = 15;

const AuditTrailPage = () => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filterEntity, setFilterEntity] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [liveCount, setLiveCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const offset = (page - 1) * ITEMS_PER_PAGE;
      const { data, count } = await auditService?.getAll({
        entityType: filterEntity || null,
        limit: ITEMS_PER_PAGE,
        offset,
      });
      setEntries(data);
      setTotal(count);
    } catch (err) {
      console.error('Audit trail load error:', err?.message);
    } finally {
      setLoading(false);
    }
  }, [page, filterEntity]);

  useEffect(() => { load(); }, [load]);

  // Real-time subscription
  useEffect(() => {
    const channel = auditService?.subscribeToNew(() => {
      setLiveCount(c => c + 1);
    });
    return () => auditService?.unsubscribe(channel);
  }, []);

  const handleExportCSV = () => {
    const cols = ['action', 'entity_type', 'entity_name', 'user_email', 'created_at'];
    const hdrs = { action: 'Action', entity_type: 'Entity Type', entity_name: 'Entity Name', user_email: 'User', created_at: 'Timestamp' };
    exportToCSV(entries, cols, hdrs, 'audit_trail.csv');
  };

  const handleExportPDF = () => {
    const cols = ['action', 'entity_type', 'entity_name', 'user_email', 'created_at'];
    const hdrs = { action: 'Action', entity_type: 'Entity Type', entity_name: 'Entity Name', user_email: 'User', created_at: 'Timestamp' };
    exportToPDF({
      title: 'Audit Trail Report',
      subtitle: `Exported on ${new Date()?.toLocaleDateString()}`,
      rows: entries,
      columns: cols,
      headers: hdrs,
      summaryCards: [
        { label: 'Total Entries', value: total },
        { label: 'This Page', value: entries?.length },
      ],
    });
  };

  const filtered = entries?.filter(e => {
    const matchAction = !filterAction || e?.action === filterAction;
    const matchSearch = !search ||
      e?.entity_name?.toLowerCase()?.includes(search?.toLowerCase()) ||
      e?.user_email?.toLowerCase()?.includes(search?.toLowerCase()) ||
      e?.action?.toLowerCase()?.includes(search?.toLowerCase());
    return matchAction && matchSearch;
  });

  const uniqueActions = [...new Set(entries.map(e => e?.action).filter(Boolean))];

  return (
    <>
      <Helmet><title>Audit Trail — PartnerHub</title></Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <Header />
        <main className="pt-[148px]">
          <div className="max-w-[1400px] mx-auto px-24 py-32">
            {/* Page Header */}
            <div className="flex items-start justify-between mb-32">
              <div>
                <div className="flex items-center gap-12 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg">
                    <Icon name="ClipboardList" size={22} className="text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-slate-900 font-heading">Audit Trail</h1>
                    <p className="text-sm text-slate-500 mt-2">Full change history across all modules</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-10">
                {liveCount > 0 && (
                  <button
                    onClick={() => { load(); setLiveCount(0); }}
                    className="flex items-center gap-8 px-16 py-10 bg-primary/10 text-primary text-sm font-semibold rounded-xl hover:bg-primary/20 transition-colors"
                  >
                    <Icon name="RefreshCw" size={14} />
                    {liveCount} new entr{liveCount === 1 ? 'y' : 'ies'}
                  </button>
                )}
                <button
                  onClick={handleExportCSV}
                  className="flex items-center gap-8 px-16 py-10 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-xl hover:bg-slate-50 transition-colors shadow-sm"
                >
                  <Icon name="Download" size={14} />
                  CSV
                </button>
                <button
                  onClick={handleExportPDF}
                  className="flex items-center gap-8 px-16 py-10 bg-gradient-to-r from-primary to-secondary text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity shadow-sm"
                >
                  <Icon name="FileText" size={14} />
                  PDF
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-16 mb-28">
              {[
                { label: 'Total Entries', value: total, icon: 'ClipboardList', color: 'from-primary to-secondary' },
                { label: 'This Page', value: filtered?.length, icon: 'List', color: 'from-emerald-500 to-teal-500' },
                { label: 'Live Updates', value: liveCount, icon: 'Zap', color: 'from-amber-500 to-orange-500' },
                { label: 'Entity Types', value: [...new Set(entries.map(e => e?.entity_type).filter(Boolean))]?.length, icon: 'Layers', color: 'from-violet-500 to-purple-500' },
              ]?.map(stat => (
                <div key={stat?.label} className="bg-white rounded-2xl border border-slate-200/60 p-20 shadow-sm">
                  <div className="flex items-center gap-12">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat?.color} flex items-center justify-center`}>
                      <Icon name={stat?.icon} size={18} className="text-white" />
                    </div>
                    <div>
                      <div className="text-xl font-bold text-slate-900">{stat?.value}</div>
                      <div className="text-xs text-slate-500">{stat?.label}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Filters */}
            <div className="bg-white rounded-2xl border border-slate-200/60 p-20 mb-20 shadow-sm">
              <div className="flex flex-wrap items-center gap-12">
                <div className="flex items-center gap-8 flex-1 min-w-[200px] bg-slate-50 border border-slate-200 rounded-xl px-14 py-10">
                  <Icon name="Search" size={16} className="text-slate-400 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="Search by entity, user, action…"
                    value={search}
                    onChange={e => setSearch(e?.target?.value)}
                    className="bg-transparent text-sm text-slate-700 placeholder-slate-400 outline-none flex-1"
                  />
                </div>
                <select
                  value={filterEntity}
                  onChange={e => { setFilterEntity(e?.target?.value); setPage(1); }}
                  className="px-14 py-10 text-sm border border-slate-200 rounded-xl bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">All Entity Types</option>
                  {['partner', 'opportunity', 'risk', 'capa_action', 'lifecycle_request', 'portfolio_product', 'portfolio_service', 'training']?.map(t => (
                    <option key={t} value={t}>{t?.replace(/_/g, ' ')}</option>
                  ))}
                </select>
                <select
                  value={filterAction}
                  onChange={e => setFilterAction(e?.target?.value)}
                  className="px-14 py-10 text-sm border border-slate-200 rounded-xl bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">All Actions</option>
                  {uniqueActions?.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                {(filterEntity || filterAction || search) && (
                  <button
                    onClick={() => { setFilterEntity(''); setFilterAction(''); setSearch(''); setPage(1); }}
                    className="flex items-center gap-6 px-14 py-10 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                  >
                    <Icon name="X" size={14} />
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-60">
                  <Icon name="Loader2" size={28} className="animate-spin text-primary" />
                </div>
              ) : filtered?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-60 text-slate-400">
                  <Icon name="ClipboardList" size={40} className="mb-12 opacity-30" />
                  <p className="text-base font-medium">No audit entries found</p>
                  <p className="text-sm mt-4">Actions across the app will appear here</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-100 bg-slate-50/50">
                          <th className="text-left px-20 py-14 text-xs font-bold text-slate-500 uppercase tracking-wider">Action</th>
                          <th className="text-left px-20 py-14 text-xs font-bold text-slate-500 uppercase tracking-wider">Entity</th>
                          <th className="text-left px-20 py-14 text-xs font-bold text-slate-500 uppercase tracking-wider">Name</th>
                          <th className="text-left px-20 py-14 text-xs font-bold text-slate-500 uppercase tracking-wider">User</th>
                          <th className="text-left px-20 py-14 text-xs font-bold text-slate-500 uppercase tracking-wider">When</th>
                          <th className="text-left px-20 py-14 text-xs font-bold text-slate-500 uppercase tracking-wider">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {filtered?.map(entry => (
                          <React.Fragment key={entry?.id}>
                            <tr className="hover:bg-slate-50/50 transition-colors">
                              <td className="px-20 py-14">
                                <span className={`inline-flex items-center px-10 py-4 rounded-lg text-xs font-bold ${ACTION_COLORS?.[entry?.action] || 'bg-slate-100 text-slate-600'}`}>
                                  {entry?.action}
                                </span>
                              </td>
                              <td className="px-20 py-14">
                                <div className="flex items-center gap-8">
                                  <Icon name={ENTITY_ICONS?.[entry?.entity_type] || 'File'} size={14} className="text-slate-400" />
                                  <span className="text-sm text-slate-600 capitalize">{entry?.entity_type?.replace(/_/g, ' ')}</span>
                                </div>
                              </td>
                              <td className="px-20 py-14">
                                <span className="text-sm font-medium text-slate-900">{entry?.entity_name || '—'}</span>
                              </td>
                              <td className="px-20 py-14">
                                <div className="flex items-center gap-8">
                                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-[9px] font-bold">
                                    {(entry?.user_profiles?.full_name || entry?.user_email || 'U')?.charAt(0)?.toUpperCase()}
                                  </div>
                                  <span className="text-sm text-slate-600">{entry?.user_profiles?.full_name || entry?.user_email || 'System'}</span>
                                </div>
                              </td>
                              <td className="px-20 py-14">
                                <span className="text-sm text-slate-500">{timeAgo(entry?.created_at)}</span>
                              </td>
                              <td className="px-20 py-14">
                                {(entry?.old_values || entry?.new_values) && (
                                  <button
                                    onClick={() => setExpandedId(expandedId === entry?.id ? null : entry?.id)}
                                    className="flex items-center gap-6 text-xs text-primary font-semibold hover:underline"
                                  >
                                    <Icon name={expandedId === entry?.id ? 'ChevronUp' : 'ChevronDown'} size={12} />
                                    {expandedId === entry?.id ? 'Hide' : 'View'}
                                  </button>
                                )}
                              </td>
                            </tr>
                            {expandedId === entry?.id && (
                              <tr>
                                <td colSpan={6} className="px-20 pb-16 bg-slate-50/50">
                                  <div className="grid grid-cols-2 gap-16 mt-4">
                                    {entry?.old_values && (
                                      <div>
                                        <p className="text-xs font-bold text-slate-500 uppercase mb-6">Before</p>
                                        <pre className="text-xs bg-red-50 border border-red-100 rounded-xl p-12 overflow-x-auto text-red-800 max-h-[120px]">
                                          {JSON.stringify(entry?.old_values, null, 2)}
                                        </pre>
                                      </div>
                                    )}
                                    {entry?.new_values && (
                                      <div>
                                        <p className="text-xs font-bold text-slate-500 uppercase mb-6">After</p>
                                        <pre className="text-xs bg-emerald-50 border border-emerald-100 rounded-xl p-12 overflow-x-auto text-emerald-800 max-h-[120px]">
                                          {JSON.stringify(entry?.new_values, null, 2)}
                                        </pre>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-20 py-16 border-t border-slate-100">
                    <Pagination
                      currentPage={page}
                      totalItems={total}
                      itemsPerPage={ITEMS_PER_PAGE}
                      onPageChange={setPage}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AuditTrailPage;
